
import { useCallback, useRef } from 'react';
import { MutableRefObject } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getUserFromSession } from '../../authOperations';
import { Session } from '@supabase/supabase-js';
import { User } from '../../types';

interface SessionCheckerOptions {
  setUser: (user: User | null) => void;
  setSession: (session: Session | null) => void;
  setIsLoading: (isLoading: boolean) => void;
  setLastError: (error: Error | null) => void;
  isMountedRef: MutableRefObject<boolean>;
}

export function useSessionChecker({
  setUser,
  setSession,
  setIsLoading,
  setLastError,
  isMountedRef
}: SessionCheckerOptions) {
  const isCheckingSessionRef = useRef(false);
  const lastSessionCheckTimeRef = useRef(0);
  
  const checkSession = useCallback(async (force: boolean = false) => {
    // Prevent multiple simultaneous session checks
    if (isCheckingSessionRef.current) {
      console.log('Session check already in progress, skipping');
      return;
    }
    
    const now = Date.now();
    // Reduce throttling when force = true - Don't check more often than every 5 seconds 
    // regardless of force parameter to prevent excessive API calls
    if (!force && now - lastSessionCheckTimeRef.current < 60000) {
      return;
    } else if (force && now - lastSessionCheckTimeRef.current < 5000) {
      console.log('Session check throttled (even with force=true), last check was', 
        (now - lastSessionCheckTimeRef.current) / 1000, 'seconds ago');
      return;
    }
    
    lastSessionCheckTimeRef.current = now;
    isCheckingSessionRef.current = true;
    setIsLoading(true);
    setLastError(null);
    
    try {
      // Check for current session
      console.log('Checking current session...');
      const { data: { session: currentSession }, error } = await supabase.auth.getSession();
      
      if (error) {
        throw error;
      }
      
      if (!isMountedRef.current) return;
      
      if (currentSession) {
        console.log('Active session found:', currentSession.user?.email);
        
        // Get user profile from session
        const userProfile = await getUserFromSession(currentSession);
        
        if (!isMountedRef.current) return;
        
        if (userProfile) {
          console.log("User profile retrieved:", userProfile.email);
          setUser(userProfile);
        } else {
          console.warn("No user profile found after session check");
          
          // Create a fallback user based on session data if profile fetch fails
          if (currentSession.user) {
            const fallbackUser: User = {
              ...currentSession.user,
              name: currentSession.user.email?.split('@')[0] || 'User',
              subscriptionTier: 'free',
              profileImage: null,
              provider: (currentSession.user.app_metadata?.provider === 'google' || 
                        currentSession.user.app_metadata?.provider === 'apple') ? 
                        currentSession.user.app_metadata.provider as 'google' | 'apple' : 'email'
            };
            setUser(fallbackUser);
          }
        }
        
        setSession(currentSession);
      } else {
        console.log("No active session found");
        setUser(null);
        setSession(null);
      }
    } catch (error) {
      if (!isMountedRef.current) return;
      
      console.error('Error checking auth session:', error);
      setLastError(error instanceof Error ? error : new Error('Unknown session error'));
    } finally {
      isCheckingSessionRef.current = false;
      
      if (isMountedRef.current) {
        // Remove delay to make the UI respond faster
        setIsLoading(false);
      }
    }
  }, [setUser, setSession, setIsLoading, setLastError, isMountedRef]);

  return { checkSession };
}
